import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { GradientButton, GhostButton } from "@/components/common";
import { VimeoPlayer } from "@/components/VimeoPlayer";
import { scrollToId } from "@/lib/scroll";
import { useCtaActions } from "@/lib/ctaActions";
import { useI18n } from "@/i18n";

const lineUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export function Hero() {
  const { goFirstListing } = useCtaActions();
  const { t } = useI18n();

  return (
    <section id="hero" className="relative overflow-visible bg-white pb-16 pt-28 md:pb-24 md:pt-36">
      {/* Subtle SaaS background pattern (grid + dots + soft red glows), faded toward edges/bottom */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(#0D0D0F 1px, transparent 1px), linear-gradient(90deg, #0D0D0F 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "radial-gradient(#6B6B70 1px, transparent 1.5px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse 70% 50% at 50% 25%, black, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 25%, black, transparent 70%)",
          }}
        />
        <div className="absolute -top-24 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-gradient-red opacity-[0.07] blur-[120px]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-white" />
      </div>

      <div className="container-re relative">
        {/* Headline (first visible element, no pill) — overflow visible, safe line-height */}
        <h1 className="mx-auto max-w-4xl px-2 pb-1 text-center font-heading text-4xl font-black leading-[1.12] tracking-tight text-re-ink sm:text-5xl lg:text-[4.25rem]">
          <motion.span variants={lineUp} custom={0} initial="hidden" animate="show" className="block">
            {t("hero.line1")}
          </motion.span>
          <motion.span variants={lineUp} custom={1} initial="hidden" animate="show" className="block">
            {t("hero.line2")} <span className="text-gradient-red">{t("hero.line2accent")}</span>.
          </motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="mx-auto mt-6 max-w-[720px] text-center text-base text-re-muted sm:text-lg"
        >
          {t("hero.sub")}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.6 }}
          className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <GradientButton testid="hero-primary-cta" onClick={goFirstListing} className="w-full sm:w-auto">
            {t("hero.cta1")} <ArrowRight className="h-4 w-4" />
          </GradientButton>
          <GhostButton testid="hero-secondary-cta" onClick={() => scrollToId("zo-werkt-het")} className="w-full sm:w-auto">
            <Play className="h-4 w-4" /> {t("hero.cta2")}
          </GhostButton>
        </motion.div>

        {/* Hero media — main homepage demo video (Vimeo). */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto mt-14 max-w-3xl"
          data-testid="hero-video"
        >
          <div className="pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-gradient-red opacity-[0.12] blur-[70px]" />
          <div className="relative overflow-hidden rounded-3xl border border-re-line bg-black shadow-premium">
            {/* HERO MEDIA CONTAINER (16:9) — main homepage explainer video (loaded eagerly, no autoplay). */}
            <div className="relative aspect-video w-full">
              <VimeoPlayer videoId="1218279912" title="Real Estate Pro" testid="hero-video-iframe" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
